import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sidebar } from "./AdminDashboard";
import { Search, Mail, Building2, DollarSign, Loader2, CreditCard, Zap, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerManagement() {
  const { isLoading: authLoading, isAuthenticated, logout } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [charging, setCharging] = useState(false);
  const [recordPaymentModalOpen, setRecordPaymentModalOpen] = useState(false);
  const [recordPaymentForm, setRecordPaymentForm] = useState({
    amount: '',
    stripe_payment_intent_id: '',
    description: 'CloudSync Pro Platform Fees'
  });
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCustomers();
    }
  }, [page, isAuthenticated]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/customers?page=${page}&limit=20`, {
        headers: getAdminAuthHeaders()
      });
      setCustomers(response.data.customers);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
    } finally {
      setLoading(false);
    }
  };

  const openChargeModal = (customer) => {
    setSelectedCustomer(customer);
    setChargeModalOpen(true);
  };

  const chargeCustomer = async () => {
    if (!selectedCustomer) return;
    setCharging(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/customer/${selectedCustomer.email}/charge`,
        {},
        { headers: getAdminAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`Successfully charged $${response.data.amount?.toFixed(2)} to ${selectedCustomer.card_brand} ****${selectedCustomer.card_last4}`);
        setChargeModalOpen(false);
        fetchCustomers();
      } else {
        toast.error(response.data.message || "Charge failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to charge customer");
    } finally {
      setCharging(false);
    }
  };

  const syncStripePayment = async (customer) => {
    try {
      toast.info(`Searching for payment method for ${customer.email}...`);
      const response = await axios.post(
        `${API}/admin/customer/${customer.email}/sync-stripe`,
        {},
        { headers: getAdminAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`Found and linked: ${response.data.card}`);
        fetchCustomers();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync payment method");
    }
  };

  const openRecordPaymentModal = (customer) => {
    setSelectedCustomer(customer);
    setRecordPaymentForm({
      amount: customer.outstanding_balance?.toFixed(2) || '',
      stripe_payment_intent_id: '',
      description: 'CloudSync Pro Platform Fees'
    });
    setRecordPaymentModalOpen(true);
  };

  const recordPayment = async () => {
    if (!selectedCustomer || !recordPaymentForm.amount) return;
    setRecording(true);
    
    try {
      const response = await axios.post(
        `${API}/admin/billing/record-payment`,
        {
          customer_email: selectedCustomer.email,
          amount: parseFloat(recordPaymentForm.amount),
          stripe_payment_intent_id: recordPaymentForm.stripe_payment_intent_id || `manual_${Date.now()}`,
          description: recordPaymentForm.description
        },
        { headers: getAdminAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setRecordPaymentModalOpen(false);
        fetchCustomers();
      } else {
        toast.error(response.data.message || "Failed to record payment");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setRecording(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-bronze" />
      </div>
    );
  }

  const filteredCustomers = customers.filter(customer => 
    customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar onLogout={logout} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-midnight font-outfit" data-testid="customers-title">
            Customers
          </h1>
          <p className="text-slate-600">Manage plugin buyers and their activity</p>
        </div>

        {/* Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search by email, name, or company..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="customer-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* Customers Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <p>No customers found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCustomers.map((customer, index) => (
              <Card key={customer.id || index} className="card-hover" data-testid={`customer-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-bronze/10 flex items-center justify-center text-bronze font-semibold">
                      {customer.name ? customer.name.charAt(0).toUpperCase() : customer.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-midnight truncate">
                        {customer.name || 'Unknown'}
                      </h3>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Mail className="w-3 h-3" />
                        <span className="truncate">{customer.email}</span>
                      </div>
                      {customer.company && (
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          <Building2 className="w-3 h-3" />
                          <span className="truncate">{customer.company}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Method Info */}
                  <div className="mt-4 p-3 rounded-lg bg-slate-50">
                    {customer.stripe_customer_id && customer.card_last4 ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-slate-700">
                            {customer.card_brand?.toUpperCase()} ****{customer.card_last4}
                          </span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          On File
                        </Badge>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-slate-500">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">No payment method</span>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={() => syncStripePayment(customer)}
                          data-testid={`sync-btn-${index}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Sync Payment
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Total Spent</p>
                      <p className="font-semibold text-midnight flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                        {formatCurrency(customer.total_spent)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Outstanding</p>
                      <p className={`font-semibold flex items-center gap-1 ${customer.outstanding_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        <DollarSign className="w-4 h-4" />
                        {formatCurrency(customer.outstanding_balance)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">
                      Joined {formatDate(customer.created_at)}
                    </span>
                    <div className="flex gap-2">
                      {customer.outstanding_balance > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-blue-600 border-blue-300 hover:bg-blue-50"
                          onClick={() => openRecordPaymentModal(customer)}
                          data-testid={`record-btn-${index}`}
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Record Payment
                        </Button>
                      )}
                      {customer.stripe_customer_id && customer.card_last4 && customer.outstanding_balance > 0 ? (
                        <Button 
                          size="sm" 
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={() => openChargeModal(customer)}
                          data-testid={`charge-btn-${index}`}
                        >
                          <Zap className="w-3 h-3 mr-1" />
                          Charge Now
                        </Button>
                      ) : customer.outstanding_balance <= 0 ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Paid Up</Badge>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button 
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </main>

      {/* Charge Confirmation Modal */}
      <Dialog open={chargeModalOpen} onOpenChange={setChargeModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="charge-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Charge Customer
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Customer</span>
                  <span className="font-medium">{selectedCustomer.name || selectedCustomer.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Email</span>
                  <span className="font-medium">{selectedCustomer.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Payment Method</span>
                  <span className="font-medium">
                    {selectedCustomer.card_brand?.toUpperCase()} ****{selectedCustomer.card_last4}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-slate-600 font-medium">Outstanding Balance</span>
                  <span className="font-bold text-lg text-amber-600">
                    ${selectedCustomer.outstanding_balance?.toFixed(2)}
                  </span>
                </div>
              </div>
              
              <p className="text-sm text-slate-500">
                This will charge the customer's card on file for their outstanding platform fees. 
                The charge will be recorded and the customer will receive a receipt.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setChargeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={chargeCustomer} 
              disabled={charging}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-charge-btn"
            >
              {charging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Charging...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Charge ${selectedCustomer?.outstanding_balance?.toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Modal */}
      <Dialog open={recordPaymentModalOpen} onOpenChange={setRecordPaymentModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="record-payment-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Record External Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                <p className="text-sm text-blue-700">
                  Use this to record a payment that was already processed externally 
                  (e.g., bank transfer, cash, or charge made from another system).
                </p>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Customer</span>
                  <span className="font-medium">{selectedCustomer.name || selectedCustomer.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Email</span>
                  <span className="font-medium">{selectedCustomer.email}</span>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Amount ($)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="55.04"
                    value={recordPaymentForm.amount}
                    onChange={(e) => setRecordPaymentForm({...recordPaymentForm, amount: e.target.value})}
                    className="mt-1"
                    data-testid="record-amount-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Stripe Payment ID (optional)</label>
                  <Input
                    type="text"
                    placeholder="pi_3TBHT7IzfpnsFhOr1LIkWVLB"
                    value={recordPaymentForm.stripe_payment_intent_id}
                    onChange={(e) => setRecordPaymentForm({...recordPaymentForm, stripe_payment_intent_id: e.target.value})}
                    className="mt-1"
                    data-testid="record-stripe-id-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">If you have the Stripe payment ID, enter it here to link the records.</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Description</label>
                  <Input
                    type="text"
                    placeholder="CloudSync Pro Platform Fees"
                    value={recordPaymentForm.description}
                    onChange={(e) => setRecordPaymentForm({...recordPaymentForm, description: e.target.value})}
                    className="mt-1"
                    data-testid="record-description-input"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordPaymentModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={recordPayment} 
              disabled={recording || !recordPaymentForm.amount}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-record-btn"
            >
              {recording ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
